import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { USER_ROLES, USER_ROLE_LABELS } from "@/lib/constants/roles";
import { Shield, User } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormValues } from "./formSchema";

interface UserBasicDataProps {
  form: UseFormReturn<FormValues>;
}

export function UserBasicData({ form }: UserBasicDataProps) {
  const { t } = useTranslation();
  const userType = form.watch("userType") ?? "accounting_firm";

  const roleConfig = {
    [USER_ROLES.USER]: {
      label: USER_ROLE_LABELS[USER_ROLES.USER],
      description: "Kann Mandanten anlegen und Buchhaltungen erledigen.",
      icon: User,
    },
    [USER_ROLES.ADMIN]: {
      label: USER_ROLE_LABELS[USER_ROLES.ADMIN],
      description: "Kann zusätzlich Nutzer anlegen und Zugriffe verwalten.",
      icon: Shield,
    },
  } as const;

  return (
    <div className="max-w-[600px] mx-auto">
      <h2 className="text-2xl font-semibold mb-6">{t("users.basic_data")}</h2>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("users.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Max Mustermann" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("users.email")}</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="max@example.com" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {userType === "accounting_firm" && (
          <FormField
            control={form.control}
            name="accountingFirmRole"
            render={({ field }) => {
              const selectedRole =
                roleConfig[field.value as keyof typeof roleConfig];
              const Icon = selectedRole?.icon;
              return (
                <FormItem>
                  <FormLabel>{t("users.role")}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value)}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue>
                          {field.value && (
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className="h-4 w-4" />}
                              {
                                roleConfig[
                                  field.value as keyof typeof roleConfig
                                ].label
                              }
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[1000]">
                      {Object.entries(roleConfig).map(([role, config]) => {
                        const RoleIcon = config.icon;
                        return (
                          <SelectItem key={role} value={role}>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <RoleIcon className="h-4 w-4" />
                                {config.label}
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {config.description}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        )}

        {userType === "company" && (
          <Alert variant="warning" className="mt-2">
            <AlertTitle>Wichtig</AlertTitle>
            <AlertDescription>
              Die E-Mail-Adresse muss ein Microsoft- oder Google-Konto sein.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
